import { currentHostInfo, isPlatformHost } from './host'

/**
 * Opt-in diagnostics for deployments where DevTools alone is not enough (for
 * example "the console login is not showing" on a real domain).
 *
 * Enable globally with `VITE_DEBUG=true` in `web/.env` (build-time: restart
 * the dev server or rebuild). Override per browser with `?debug=1` or
 * `?debug=0` on any URL; the choice persists in `localStorage` under
 * `bankalee:debug` (`'1'` / `'0'`). Nothing is logged unless enabled.
 */
const STORAGE_KEY = 'bankalee:debug'

let installed = false

function buildDefault(): boolean {
  return import.meta.env.VITE_DEBUG === 'true'
}

export function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false

  try {
    const params = new URLSearchParams(window.location.search)
    if (params.has('debug')) {
      const on = params.get('debug') !== '0' && params.get('debug') !== 'false'
      window.localStorage.setItem(STORAGE_KEY, on ? '1' : '0')
      return on
    }

    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === '1') return true
    if (stored === '0') return false

    return buildDefault()
  } catch {
    return buildDefault()
  }
}

export function debug(scope: string, message: string, data?: unknown): void {
  if (!isDebugEnabled()) return

  const prefix = `[bankalee:${scope}]`
  if (data === undefined) console.warn(prefix, message)
  else console.warn(prefix, message, data)
}

/** Logs the routing/config snapshot and captures uncaught errors. */
export function installDebugDiagnostics(): void {
  if (installed || !isDebugEnabled() || typeof window === 'undefined') return

  installed = true

  const host = currentHostInfo()

  console.warn('[bankalee:debug] diagnostics enabled (VITE_DEBUG=true or ?debug=1; use ?debug=0 to disable)')
  console.warn('[bankalee:host]', {
    href: window.location.href,
    host: host.host,
    subdomain: host.subdomain,
    platform: isPlatformHost(),
    expectedPlatformHost: 'admin.<base domain>',
  })

  window.addEventListener('error', (event: ErrorEvent) => {
    console.error('[bankalee:error]', event.error ?? event.message)
  })

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    console.error('[bankalee:rejection]', event.reason)
  })
}
